import { useMemo, useState } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog"
import { BarChart } from "./charts/Charts"
import { useLocale } from "./LocaleProvider"
import { useWorkerActivity, useWorkerActivityHistory } from "../lib/queries"
import { windowSeconds } from "../lib/format"
import type { WorkerActivityRecord, WorkerDailyRecord } from "../lib/api"

const RANGES = [7, 30, 90]

interface OperatorAnalyticsModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface OperatorStats {
  userId: number
  fullName: string
  email: string
  totalHeartbeats: number
  activeDays: number
  totalWindow: number
  todayHeartbeats: number
  online: boolean
}

interface TeamSummary {
  totalHeartbeats: number
  activeDays: number
  operatorCount: number
  activeOperatorCount: number
  avgPerActiveDay: number
  avgPerOperatorPerDay: number
  best: OperatorStats | null
}

function OperatorAnalyticsModal({ open, onOpenChange }: OperatorAnalyticsModalProps) {
  const { t } = useLocale()
  const [range, setRange] = useState(30)
  const { data: history, isLoading } = useWorkerActivityHistory(open ? range : 0, open)
  const { data: live } = useWorkerActivity(open ? 10000 : 0)

  const stats: OperatorStats[] = useMemo(() => {
    const rows: WorkerDailyRecord[] = history ?? []
    const byUser = new Map<number, OperatorStats>()

    for (const r of rows) {
      let s = byUser.get(r.userId)
      if (!s) {
        s = {
          userId: r.userId,
          fullName: r.fullName,
          email: r.email,
          totalHeartbeats: 0,
          activeDays: 0,
          totalWindow: 0,
          todayHeartbeats: 0,
          online: false,
        }
        byUser.set(r.userId, s)
      }
      s.totalHeartbeats += r.heartbeatCount
      if (r.heartbeatCount > 0) s.activeDays += 1
      s.totalWindow += windowSeconds(r.firstSeenAt, r.lastSeenAt)
    }

    const liveMap = new Map<number, WorkerActivityRecord>()
    for (const rec of live ?? []) liveMap.set(rec.userId, rec)
    const userList = Array.from(byUser.values())
    for (const s of userList) {
      const rec = liveMap.get(s.userId)
      if (rec) {
        s.todayHeartbeats = rec.todayHeartbeats
        s.online = rec.online
      }
    }

    return userList
  }, [history, live])

  const team: TeamSummary = useMemo(() => {
    const totalHeartbeats = stats.reduce((sum, s) => sum + s.totalHeartbeats, 0)
    const activeDays = stats.reduce((sum, s) => sum + s.activeDays, 0)
    const activeStats = stats.filter((s) => s.activeDays > 0)
    const activeOperatorCount = activeStats.length
    const avgPerOperatorPerDay =
      activeOperatorCount > 0
        ? activeStats.reduce((sum, s) => sum + s.totalHeartbeats / s.activeDays, 0) /
          activeOperatorCount
        : 0
    return {
      totalHeartbeats,
      activeDays,
      operatorCount: stats.length,
      activeOperatorCount,
      avgPerActiveDay: activeDays > 0 ? totalHeartbeats / activeDays : 0,
      avgPerOperatorPerDay,
      best: stats.reduce<OperatorStats | null>(
        (best, s) => (best === null || s.totalHeartbeats > best.totalHeartbeats ? s : best),
        null,
      ),
    }
  }, [stats])

  const sorted = useMemo(
    () => [...stats].sort((a, b) => b.totalHeartbeats - a.totalHeartbeats),
    [stats],
  )

  const chartData = useMemo(
    () =>
      stats
        .map((s) => ({ label: s.fullName || "—", value: s.totalHeartbeats }))
        .sort((a, b) => b.value - a.value),
    [stats],
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("activity_analytics_title")}</DialogTitle>
          <p className="text-sm text-muted-foreground">
            {t("activity_effectiveness_hint")} · {t("activity_range")}
          </p>
        </DialogHeader>

        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">{t("activity_range")}</span>
          <div className="flex items-center gap-1">
            {RANGES.map((r) => (
              <button
                key={r}
                onClick={() => setRange(r)}
                className={`h-7 rounded-md border px-2.5 text-xs font-medium transition-colors ${
                  range === r
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-input bg-background text-muted-foreground hover:bg-accent hover:text-foreground"
                }`}
              >
                {r}d
              </button>
            ))}
          </div>
        </div>

        {isLoading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">{t("loading")}</div>
        ) : sorted.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">{t("activity_empty")}</div>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Tile
                label={t("activity_team_total_requests")}
                value={String(team.totalHeartbeats)}
              />
              <Tile
                label={t("activity_avg_operator_day")}
                value={formatRequests(team.avgPerOperatorPerDay)}
              />
              <Tile
                label={t("activity_active_operators")}
                value={`${team.activeOperatorCount} / ${team.operatorCount}`}
              />
              <Tile
                label={t("activity_best_operator")}
                value={team.best?.fullName ?? "—"}
                sub={team.best ? `${t("activity_total_heartbeats")}: ${team.best.totalHeartbeats}` : undefined}
              />
            </div>

            <div>
              <div className="mb-2 text-sm font-medium">{t("activity_requests_by_operator")}</div>
              <BarChart data={chartData} formatValue={(v) => String(Math.round(v))} />
            </div>

            <div className="rounded-lg border">
              <div className="grid grid-cols-[2.5rem_1.4fr_3.5rem_4.5rem_4.5rem_3.5rem_3rem_9rem] items-center gap-2 border-b px-4 py-2 text-xs font-medium text-muted-foreground">
                <span>{t("activity_rank")}</span>
                <span>{t("activity_worker")}</span>
                <span className="text-right">{t("activity_active_days")}</span>
                <span className="text-right">{t("activity_avg_per_day")}</span>
                <span className="text-right">{t("activity_total_heartbeats")}</span>
                <span className="text-right">{t("activity_today")}</span>
                <span className="text-right">{t("activity_share")}</span>
                <span className="text-right">
                  {t("activity_effectiveness")}
                  <span className="block text-[10px] font-normal">
                    {t("activity_effectiveness_hint")}
                  </span>
                </span>
              </div>

              <div className="max-h-[50vh] overflow-y-auto">
                {sorted.map((s, i) => {
                  const avgReqs = s.activeDays > 0 ? s.totalHeartbeats / s.activeDays : 0
                  const eff =
                    s.activeDays > 0 && team.avgPerActiveDay > 0
                      ? (s.totalHeartbeats / s.activeDays) / team.avgPerActiveDay
                      : null
                  const effPct = eff ? Math.round(eff * 100) : null
                  const diff = effPct != null ? effPct - 100 : null
                  const share =
                    team.totalHeartbeats > 0 ? Math.round((s.totalHeartbeats / team.totalHeartbeats) * 100) : 0
                  return (
                    <div
                      key={s.userId}
                      className="grid grid-cols-[2.5rem_1.4fr_3.5rem_4.5rem_4.5rem_3.5rem_3rem_9rem] items-center gap-2 border-b px-4 py-2 text-sm last:border-b-0"
                    >
                      <span className={`font-medium ${i === 0 ? "text-amber-500" : i === 1 ? "text-slate-400" : i === 2 ? "text-orange-400" : "text-muted-foreground"}`}>
                        #{i + 1}
                      </span>
                      <span className="flex min-w-0 items-center gap-1.5">
                        <span
                          className={`h-1.5 w-1.5 shrink-0 rounded-full ${
                            s.online ? "bg-emerald-500" : "bg-muted-foreground/40"
                          }`}
                        />
                        <span className="truncate font-medium">{s.fullName}</span>
                      </span>
                      <span className="text-right text-muted-foreground">{s.activeDays}</span>
                      <span className="text-right font-medium">{formatRequests(avgReqs)}</span>
                      <span className="text-right font-medium">{s.totalHeartbeats}</span>
                      <span className="text-right text-muted-foreground">{s.todayHeartbeats}</span>
                      <span className="text-right text-muted-foreground">{share}%</span>
                      <span className="flex items-center justify-end gap-1.5">
                        <span className="relative h-1.5 w-full max-w-[5rem] overflow-hidden rounded-full bg-muted">
                          <span
                            className={`absolute inset-y-0 left-0 rounded-full ${
                              effPct != null && effPct >= 100
                                ? "bg-emerald-500/80"
                                : "bg-amber-500/80"
                            }`}
                            style={{ width: `${Math.min(100, effPct ?? 0)}%` }}
                          />
                        </span>
                        <span
                          className={`w-14 text-right text-xs font-medium ${
                            diff != null && diff >= 0
                              ? "text-emerald-600 dark:text-emerald-400"
                              : "text-muted-foreground"
                          }`}
                        >
                          {eff == null
                            ? "—"
                            : diff != null && diff >= 0
                              ? `+${diff}%`
                              : `${diff}%`}
                        </span>
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

function formatRequests(v: number): string {
  if (!Number.isFinite(v) || v <= 0) return "—"
  return v >= 10 ? String(Math.round(v)) : v.toFixed(1)
}

function Tile({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-lg border bg-card p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 truncate text-lg font-semibold" title={value}>
        {value}
      </p>
      {sub && <p className="truncate text-xs text-muted-foreground">{sub}</p>}
    </div>
  )
}

export default OperatorAnalyticsModal